import { DEMO_PROJECT } from './demo-project';
import type { GgxTourDefinition } from './tour-types';

export const RISK_07: GgxTourDefinition = {
    key: '[RISK-07] Measuring Faithfulness/Groundedness',
    route: '/home',
    project: DEMO_PROJECT,
    estimatedMinutes: 8,
    prerequisite: {
        target: '[test-id="search-button"]',
        message: 'Open the home page, then start this tour.',
    },
    intro: {
        headline: 'Does the answer stick to the facts it was given?',
        subtitle:
            'Faithfulness asks one narrow question: is every claim in the reply supported by ' +
            "the information the model was handed? When the chatbot looks up a customer's " +
            'cards and then answers, a faithful reply mentions only those cards, those ' +
            'balances, those fees. Anything else is invented, however plausible it sounds. ' +
            'This tour shows how faithfulness is checked on the credit card chatbot, reads its ' +
            'faithfulness report, and then compares it with a version of the same pipeline ' +
            'that has a fact-check guard built in.',
    },
    outro: {
        title: "That's a wrap!",
        subtitle:
            'You have seen what faithfulness measures and what it leaves out, how the check ' +
            'works by comparing the card facts in a reply with the data the model was given, ' +
            'and how a rule-based test like this runs fast enough to double as a guardrail. ' +
            'You also compared two pipelines side by side: the original, and the same design ' +
            'with a fact-check helper that validates each statement before it goes out. That ' +
            'is the pattern for every risk in this track: measure it, add a control, measure ' +
            'again. The other risk tours in the Learning Hub do the same for accuracy, ' +
            'vulnerability and toxicity.',
        learned: [
            '**Faithfulness** means grounded in the retrieved data, nothing more.',
            'The check **compares card facts** in a reply with the data given.',
            'A **fact-check helper** turns the same test into a guardrail.',
        ],
    },
    chapters: [
        {
            name: 'Find the pipeline',
            blurb: 'Search from anywhere and jump straight to it',
            icon: 'search',
            steps: [2, 3, 4],
        },
        {
            name: 'What faithfulness means',
            blurb: 'Grounded in the retrieved data, nothing more',
            icon: 'cap',
            steps: [5],
        },
        {
            name: 'Read the report',
            blurb: 'Method, metrics, and the chart by intent',
            icon: 'grid',
            steps: [6, 7, 8, 9, 10, 11, 12],
        },
        {
            name: 'Compare with the guarded pipeline',
            blurb: 'The same design with a fact-check helper added',
            icon: 'pulse',
            steps: [13, 14, 15, 16, 17, 18],
        },
    ],
    steps: [
        {
            target: '[test-id="search-button"]',
            title: 'Start from the search box',
            whatHappens: 'a search box opens in the middle of the screen.',
            icon: 'search',
            description:
                'We will read a real faithfulness report, so let us find the pipeline first. ' +
                'The global search finds any object by name from any page, and ' +
                '<strong>Ctrl+K</strong> opens it too. Click <strong>Search</strong>.',
            side: 'bottom',
            align: 'end',
            buttons: ['previous'],
            advanceOnTargetClick: true,
            actionLabel: 'Search',
        },
        {
            target: '[test-id="global-search"] input',
            title: 'Type the name of the chatbot',
            whatHappens:
                'matching objects appear below the box as you type, and the tour moves on when ' +
                'the pipeline is among them.',
            icon: 'search',
            description:
                'Click the tag to copy the name, paste it into the search box, and watch the ' +
                'results narrow down.<br><br>' +
                '<code data-ggx-copy>Combined Customer Intent and Response Pipeline</code>',
            side: 'bottom',
            align: 'start',
            buttons: ['previous'],
            advanceOnTargetMount: true,
            waitCue: 'Type the name to continue',
        },
        {
            target: '[test-id^="global-search-option-"]',
            targetText: 'Combined Customer Intent and Response Pipeline v1',
            scrollIntoView: true,
            title: 'Pick the pipeline itself',
            whatHappens: "the pipeline's page opens on its Details tab.",
            icon: 'search',
            description:
                'A few objects share this name. The one we want is the plain pipeline marked ' +
                '<strong>v1</strong>; we will come back for the fact-check variant later. Click ' +
                '<strong>Combined Customer Intent and Response Pipeline</strong>.',
            side: 'right',
            align: 'start',
            buttons: ['previous'],
            advanceOnTargetClick: true,
            actionLabel: 'Combined Customer Intent and Response Pipeline',
        },
        {
            title: 'Where faithfulness fits in the pipeline',
            whatHappens: 'next, the dashboard where the faithfulness report lives.',
            icon: 'cap',
            description:
                'Follow the diagram. A customer asks a question, the intent is classified, and ' +
                'for a card-related request the pipeline queries the customer card database and ' +
                'hands the retrieved rows to the response model. The faithfulness check sits on ' +
                'that last step: it compares the card details in the generated reply with the ' +
                'retrieved data and asks whether every one of them is actually there. Only ' +
                'details grounded in the retrieved rows count as faithful, which is what keeps ' +
                'the reply free of invented cards, balances and fees. Click Continue.',
            image: {
                src: 'https://genguardx.ai/tours/risk-07-pipeline-overview.png',
                title: 'Overview of the conversational AI chat pipeline',
                caption: 'Where the faithfulness check compares the reply with the retrieved data.',
            },
        },
        {
            target: '[test-id="tab-item-Dashboard"]',
            scrollIntoView: true,
            title: 'Where the reports live',
            whatHappens: 'the Dashboard opens on the latest run, with one tab per report.',
            icon: 'grid',
            description:
                'The latest evaluation run on this pipeline produced several reports. The one ' +
                'we want is <strong>Response Faithfulness (Credit Card Queries)</strong>. Click ' +
                '<strong>Dashboard</strong>.',
            side: 'bottom',
            align: 'start',
            buttons: ['previous'],
            advanceOnTargetClick: true,
            actionLabel: 'Dashboard',
        },
        {
            target: '[test-id^="jobtab-Response Faithfulness"]',
            scrollIntoView: true,
            title: 'Open the faithfulness report',
            whatHappens: 'the Response Faithfulness report opens on its executive summary.',
            icon: 'grid',
            description:
                'Every faithfulness test run on this pipeline is collected here. Click ' +
                '<strong>Response Faithfulness (Credit Card Queries)</strong>.',
            side: 'bottom',
            align: 'start',
            buttons: ['previous'],
            advanceOnTargetClick: true,
            actionLabel: 'Response Faithfulness (Credit Card Queries)',
        },
        {
            target: '[test-id="figure-Executive Summary"] h1',
            frame: true,
            scrollIntoView: true,
            title: 'What this report tests',
            whatHappens: 'next, the two metrics it reports.',
            icon: 'grid',
            description:
                'The chatbot is tested on how well it sticks to the data it was given. It must ' +
                'never reference credit card information that does not exist, so each test ' +
                'takes the facts in the reply and compares them with the card data that was ' +
                'retrieved and passed in. Running that across the different intents and ' +
                'sub-intents shows how faithfully the bot behaves in each kind of card ' +
                'conversation. Click Continue.',
            side: 'bottom',
            align: 'start',
            buttons: ['previous', 'next'],
        },
        {
            target: '[test-id="figure-Executive Summary"] h2',
            targetText: 'Metric Definitions',
            section: true,
            scrollIntoView: true,
            title: 'Two numbers to read',
            whatHappens: 'next, exactly how the comparison is made.',
            icon: 'grid',
            description:
                '<ul data-ggx-list>' +
                '<li><strong>Faithfulness</strong> is a yes or no per reply: 1 when every ' +
                'fact in the response matches the retrieved data with nothing added or ' +
                'changed, 0 when it does not. Simply put, whether the model stuck to the ' +
                'facts.</li>' +
                '<li><strong>Average Faithfulness per Intent</strong> is the mean of those ' +
                'scores across the test set, split by intent, so you can see where the bot ' +
                'stays factual and where it drifts.</li>' +
                '</ul>' +
                'Click Continue.',
            side: 'top',
            align: 'start',
            buttons: ['previous', 'next'],
        },
        {
            title: 'How the comparison is made',
            whatHappens: 'next, a few things to keep in mind about this test.',
            icon: 'cap',
            description:
                'The graphic shows one test:' +
                '<ul data-ggx-list>' +
                '<li>The response sub-pipeline receives the <strong>question</strong> and the ' +
                '<strong>retrieved cards</strong> for that customer.</li>' +
                '<li>Its reply carries <strong>tagged data</strong>: the card type, last four ' +
                'digits and balance it used, as structured values.</li>' +
                '<li>Those tags are lined up against the retrieved rows and <strong>each one ' +
                'is checked</strong>.</li>' +
                '</ul>' +
                '<aside data-ggx-use="In the example">One card matches fully while the other ' +
                'has a balance the data does not support, so the reply is scored ' +
                'unfaithful.</aside>' +
                'Click Continue.',
            image: {
                src: 'https://genguardx.ai/tours/risk-07-faithfulness-test.png',
                title: 'Factual faithfulness test for the conversational AI chatbot pipeline',
                caption: 'Tagged values in the reply, checked against the retrieved card rows.',
            },
        },
        {
            title: 'A few things to remember',
            whatHappens: 'next, the chart that puts the results by intent.',
            icon: 'list',
            description:
                '<ul data-ggx-list>' +
                '<li>Faithfulness does not judge whether the <strong>right data was ' +
                'retrieved</strong> or whether the reply is a good answer. It only checks that ' +
                'the reply stayed within the data it was given.</li>' +
                '<li>The logic extends to <strong>multi-turn</strong> conversations, ' +
                '<strong>computed values</strong> like "you have two cards", questions with ' +
                '<strong>several acceptable answers</strong>, and messages that ask ' +
                '<strong>more than one question</strong>.</li>' +
                '<li>The response pipeline can be changed to produce a <strong>single ' +
                'output</strong> that combines the tagged data and the text, so the numbers in ' +
                'the sentence can never drift from the tags behind them.</li>' +
                '</ul>' +
                '<aside data-ggx-use="In practice">Pair faithfulness with the accuracy report: ' +
                'accuracy asks whether the right data came back, faithfulness asks whether ' +
                'the reply stayed inside it. A bot can pass one and fail the other.</aside>' +
                'Click Continue.',
        },
        {
            target: '[test-id^="figure-Faithfulness by Intent"]',
            frame: true,
            scrollIntoView: true,
            title: 'Fast enough to be a guardrail',
            whatHappens: 'next, the same pipeline with a fact-check helper added.',
            icon: 'pulse',
            description:
                'This is faithfulness by intent for the original pipeline. Notice the ' +
                '<strong>Rule-based</strong> tag: because the check needs no external ground ' +
                'truth, only the data the model was given, it is a plain comparison that runs ' +
                'in seconds. That makes it useful twice over: as an evaluation, and as a ' +
                'guardrail that catches hallucinated facts before a deeper review or a human ' +
                'ever sees them. <strong>Goto Report</strong> opens the report definition and ' +
                'the building blocks behind it. Now let us see what a guard does to these ' +
                'numbers. Click Continue.',
            side: 'left',
            align: 'start',
            buttons: ['previous', 'next'],
        },
        {
            target: '[test-id="search-button"]',
            title: 'Find the guarded version',
            whatHappens: 'the search box opens again.',
            icon: 'search',
            description:
                'The team built a second version of this pipeline with a helper function that ' +
                'validates each factual statement against the customer data before the reply ' +
                'goes out. The quickest way there is the search. Click <strong>Search</strong>.',
            side: 'bottom',
            align: 'end',
            buttons: ['previous'],
            advanceOnTargetClick: true,
            actionLabel: 'Search',
        },
        {
            target: '[test-id="global-search"] input',
            title: 'Type the name of the guarded pipeline',
            whatHappens: 'the tour moves on when the guarded pipeline is among the results.',
            icon: 'search',
            description:
                'Click the tag to copy the name and paste it into the search box.<br><br>' +
                '<code data-ggx-copy>Combined Customer Intent and Response Pipeline (With Fact ' +
                'Check Guards)</code>',
            side: 'bottom',
            align: 'start',
            buttons: ['previous'],
            advanceOnTargetMount: true,
            waitCue: 'Type the name to continue',
        },
        {
            target: '[test-id^="global-search-option-"]',
            targetText: 'With Fact Check Guards) v1',
            scrollIntoView: true,
            title: 'Open the guarded pipeline',
            whatHappens: "the guarded pipeline's page opens on its Details tab.",
            icon: 'search',
            description:
                'Same intent classifier, same response model, plus a fact-check guard. Click ' +
                '<strong>Combined Customer Intent and Response Pipeline (With Fact Check ' +
                'Guards)</strong>.',
            side: 'right',
            align: 'start',
            buttons: ['previous'],
            advanceOnTargetClick: true,
            actionLabel: 'Combined Customer Intent and Response Pipeline (With Fact Check Guards)',
        },
        {
            target: '[test-id="tab-item-Dashboard"]',
            scrollIntoView: true,
            title: 'Its own dashboard',
            whatHappens: 'the Dashboard opens on the latest run of the guarded pipeline.',
            icon: 'grid',
            description:
                'This pipeline was evaluated with the same faithfulness report, so the two ' +
                'results are directly comparable. Click <strong>Dashboard</strong>.',
            side: 'bottom',
            align: 'start',
            buttons: ['previous'],
            advanceOnTargetClick: true,
            actionLabel: 'Dashboard',
        },
        {
            target: '[test-id^="jobtab-Response Faithfulness"]',
            scrollIntoView: true,
            title: 'Open its faithfulness report',
            whatHappens: 'the report opens on its executive summary.',
            icon: 'grid',
            description: 'Click <strong>Response Faithfulness (Credit Card Queries)</strong>.',
            side: 'bottom',
            align: 'start',
            buttons: ['previous'],
            advanceOnTargetClick: true,
            actionLabel: 'Response Faithfulness (Credit Card Queries)',
        },
        {
            target: '[test-id^="figure-Faithfulness by Intent"]',
            frame: true,
            scrollIntoView: true,
            title: 'Before and after the guard',
            whatHappens: 'the tour wraps up.',
            icon: 'pulse',
            description:
                'Compare this chart with the one you saw a minute ago. The guard checks every ' +
                'card detail against the retrieved data before the reply is sent and rewrites ' +
                'or blocks anything unsupported, so the intents that dipped on the original ' +
                'should sit at or near 100% here. That is the whole loop in one screen: ' +
                'measure a risk, add a control, measure again to prove the control works. Click ' +
                'Continue to finish.',
            side: 'left',
            align: 'start',
            buttons: ['previous', 'next'],
        },
    ],
};
